import { useTranslation } from 'react-i18next';
import { Job } from '@vromo/models';

export interface UIStateInterface {
    statusTitle: string;
    statusDescription?: string;
    progress: number;
    stepIx: number;
}

export const getOrderSteps = (t: (id: string) => string): UIStateInterface[] => [
    {
        statusTitle: t('orderCreated'),
        statusDescription: t('orderCreatedDescription'),
        progress: 1,
        stepIx: 0
    },
    {
        statusTitle: t('orderConfirmed'),
        statusDescription: t('orderConfirmedDescription'),
        progress: 1,
        stepIx: 1
    },
    {
        statusTitle: t('pickingUpOrder'),
        statusDescription: t('pickingUpOrderDescription'),
        progress: 15,
        stepIx: 2
    },
    {
        statusTitle: t('driverWaitingForOrder'),
        statusDescription: t('driverWaitingForOrderDescription'),
        progress: 32,
        stepIx: 3
    },
    {
        statusTitle: t('headingToYou'),
        statusDescription: t('headingToYouDescription'),
        progress: 47,
        stepIx: 4
    },
    {
        statusTitle: t('driverNearby'),
        statusDescription: t('driverNearbyDescription'),
        progress: 65,
        stepIx: 5
    },
    {
        statusTitle: t('delivered'),
        statusDescription: t('deliveredDescription'),
        progress: 100,
        stepIx: 6
    }
];

export const getUIStateFromJob = (job: Job, t: (id: string) => string): UIStateInterface => {
    const orderSteps = getOrderSteps(t);

    if (job?.cancelled) {
        return {
            statusTitle: t('cancelled'),
            statusDescription: t('cancelledDescription'),
            progress: 0,
            stepIx: 0
        };
    }
    if (job?.finished) {
        const deliveryAddress = job?.tasks?.items.at(-1)?.zone?.address || 'Unknown';
        const step = { ...orderSteps[6] } as UIStateInterface;
        step.statusTitle = step.statusTitle + ` at ${deliveryAddress}`;
        return step as UIStateInterface;
    }
    if (job?.tasks?.items[2]?.completed) return orderSteps[5] as UIStateInterface;
    if (job?.tasks?.items[1]?.completed) return orderSteps[4] as UIStateInterface;
    if (job?.tasks?.items[0]?.completed) return orderSteps[3] as UIStateInterface;
    if (job.status === 'underway') return orderSteps[2] as UIStateInterface;
    if (job.status === 'dispatched') return orderSteps[1] as UIStateInterface;

    return orderSteps[0] as UIStateInterface;
};

const useUIState = (job: Job): UIStateInterface => {
    const { t } = useTranslation();

    return getUIStateFromJob(job, t);
};

export default useUIState;
